import { BriefcaseConveyorBelt } from "lucide-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { Badge } from "../../../../components/badge";
import { Button } from "../../../../components/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../components/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/table";
import CommonPagination from "../../../../components/common/CommonPagination";
import { ProgramEditor, ProgramFormValues } from "./ProgramEditor";
import { ProgramListItem } from "../../types";
import {
  CAMPAIGN_STATUS_LABELS,
  normalizeCampaignStatus,
} from "../../types/campaign-status.constants";

interface ProgramTableProps {
  programs: ProgramListItem[];
  isLoading: boolean;
  selectedProgramId: string;
  onSelectProgram: (programId: string) => void;
  onUpdateProgram: (programId: string, payload: ProgramFormValues) => void;
  onOpenProgramDetail: (programId: string) => void;
  onPaginationChange: () => void;
}

export function ProgramTable({
  programs,
  isLoading,
  selectedProgramId,
  onSelectProgram,
  onUpdateProgram,
  onOpenProgramDetail,
  onPaginationChange,
}: ProgramTableProps) {
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const totalPages = Math.max(1, Math.ceil(programs.length / itemsPerPage));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    setPage((current) => Math.min(Math.max(current, 1), totalPages));
  }, [totalPages]);

  const visiblePrograms = useMemo(() => {
    const startIndex = (safePage - 1) * itemsPerPage;
    return programs.slice(startIndex, startIndex + itemsPerPage);
  }, [programs, safePage, itemsPerPage]);

  if (isLoading) {
    return (
      <div className="px-4 py-8 text-sm text-[#6A7282]">
        Loading programs...
      </div>
    );
  }

  if (programs.length === 0) {
    return (
      <div className="px-4 py-8 text-sm text-[#6A7282]">
        No programs found for this campaign.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Program Name</TableHead>
            <TableHead>Description</TableHead>
            <TableHead className="w-[18%]">Status</TableHead>
            <TableHead className="w-[14%] text-right">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visiblePrograms.map((program) => {
            const isSelected = selectedProgramId === program.id;
            return (
              <Fragment key={program.id}>
                <TableRow
                  onClick={() => onSelectProgram(program.id)}
                  className={`cursor-pointer ${isSelected ? "bg-[#F8FFFA]" : ""}`}
                >
                  <TableCell className="font-medium text-[#0F172A]">
                    {program.name}
                  </TableCell>
                  <TableCell className="text-sm text-[#6A7282]">
                    {program.description || "No description"}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className="border-[#DDEBE3] bg-[#F3FBF6] text-[#004232]"
                    >
                      {CAMPAIGN_STATUS_LABELS[normalizeCampaignStatus(program.status)]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <TooltipProvider delayDuration={120}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              onOpenProgramDetail(program.id);
                            }}
                            aria-label={`Open details for ${program.name}`}
                            className="h-8 w-8 p-0 border-[#DDEBE3] text-[#004232] hover:bg-[#F3FBF6]"
                          >
                            <BriefcaseConveyorBelt className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit program</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                </TableRow>

                {isSelected ? (
                  <TableRow>
                    <TableCell colSpan={4} className="bg-[#FAFCFB] px-4 py-4">
                      <ProgramEditor
                        selectedProgram={program}
                        onSubmit={(value) => onUpdateProgram(program.id, value)}
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex flex-col gap-2 border-t border-[#E6EFE9] px-4 pb-4 pt-2">
        <div className="text-sm text-[#6A7282]">
          Showing {(safePage - 1) * itemsPerPage + 1}-
          {Math.min(safePage * itemsPerPage, programs.length)} of {programs.length}
        </div>
        <CommonPagination
          page={safePage}
          totalPages={totalPages}
          onPageChange={(nextPage) => {
            setPage(nextPage);
            onPaginationChange();
          }}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={(nextSize) => {
            setPage(1);
            setItemsPerPage(nextSize);
            onPaginationChange();
          }}
        />
      </div>
    </div>
  );
}
